// v10/src/components/admin/EmergencyProcedureManager.js
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2, Edit2, Camera, GripVertical, AlertTriangle, ChevronDown, ChevronUp, Upload, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { emergencyService, aircraftService } from '../../services/apiService';
import ConfirmModal from '../common/ConfirmModal';

const EMERGENCY_TYPES = {
  engine_failure: { label: 'Falla de motor',         emoji: '🔴', color: '#ef4444' },
  fire:           { label: 'Fuego en cabina/motor',   emoji: '🔥', color: '#f97316' },
  electrical:     { label: 'Falla eléctrica',         emoji: '⚡', color: '#f59e0b' },
  ditching:       { label: 'Amerizaje de emergencia', emoji: '🌊', color: '#0891b2' },
  gear:           { label: 'Tren de aterrizaje',      emoji: '⚙️', color: '#7c3aed' },
  other:          { label: 'Otro procedimiento',      emoji: '⚠️', color: '#64748b' },
};

// ── Drag & drop step editor ───────────────────────────────────────
const StepRow = ({ step, index, onUpdate, onRemove, total }) => (
  <motion.div layout style={{ display:'flex', gap:8, alignItems:'flex-start', padding:'10px 12px', background:'rgba(255,255,255,0.6)', borderRadius:14, border:'1px solid rgba(226,232,240,0.8)' }}>
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4, paddingTop:6 }}>
      <GripVertical size={14} color="#94a3b8" />
      <span style={{ fontSize:11, fontWeight:800, color:'#64748b' }}>{index + 1}</span>
    </div>
    <div style={{ flex:1, display:'flex', flexDirection:'column', gap:6 }}>
      <input
        value={step.text} placeholder="Texto del paso"
        onChange={e => onUpdate(index, { ...step, text: e.target.value })}
        style={{ width:'100%', padding:'8px 12px', borderRadius:10, border:'1px solid #e2e8f0', fontSize:14, fontFamily:'inherit', fontWeight:600 }}
      />
      <input
        value={step.detail || ''} placeholder="Detalle (ej: Vx = 65 KIAS)"
        onChange={e => onUpdate(index, { ...step, detail: e.target.value })}
        style={{ width:'100%', padding:'6px 12px', borderRadius:10, border:'1px solid #e2e8f0', fontSize:13, fontFamily:'inherit', color:'#475569' }}
      />
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'center' }}>
        <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:12, fontWeight:600, color: step.is_critical ? '#dc2626' : '#64748b', cursor:'pointer' }}>
          <input type="checkbox" checked={step.is_critical || false} onChange={e => onUpdate(index, { ...step, is_critical: e.target.checked, is_flexible: e.target.checked ? false : step.is_flexible })} />
          Crítico
        </label>
        <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:12, fontWeight:600, color: step.is_flexible ? '#0891b2' : '#64748b', cursor:'pointer' }}>
          <input type="checkbox" checked={step.is_flexible || false} onChange={e => onUpdate(index, { ...step, is_flexible: e.target.checked, is_critical: e.target.checked ? false : step.is_critical })} />
          Flexible
        </label>
        <div style={{ display:'flex', alignItems:'center', gap:4 }}>
          <span style={{ fontSize:12, color:'#64748b' }}>Benchmark:</span>
          <input type="number" value={step.benchmark_seconds || ''} min={1} max={120} placeholder="seg"
            onChange={e => onUpdate(index, { ...step, benchmark_seconds: parseInt(e.target.value) || null })}
            style={{ width:60, padding:'4px 8px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:12, fontFamily:'inherit', textAlign:'center' }}
          />
        </div>
      </div>
    </div>
    <button onClick={() => onRemove(index)} style={{ background:'none', border:'none', cursor:'pointer', padding:4 }}>
      <Trash2 size={16} color="#ef4444" />
    </button>
  </motion.div>
);

// ── Procedure Form Modal ──────────────────────────────────────────
const ProcedureFormModal = ({ procedure, aircraftId, onSave, onClose }) => {
  const [title, setTitle] = useState(procedure?.title || '');
  const [emergencyType, setEmergencyType] = useState(procedure?.emergency_type || 'engine_failure');
  const [description, setDescription] = useState(procedure?.description || '');
  const [benchmarkSeconds, setBenchmarkSeconds] = useState(procedure?.benchmark_seconds || 30);
  const [items, setItems] = useState(procedure?.items?.map(i => ({ ...i })) || [{ step_number: 1, text: '', is_critical: true, benchmark_seconds: 3 }]);
  const [saving, setSaving] = useState(false);

  const updateItem = (index, updated) => setItems(prev => prev.map((s, i) => i === index ? updated : s));
  const removeItem = (index) => setItems(prev => prev.filter((_, i) => i !== index));
  const addItem = () => setItems(prev => [...prev, { step_number: prev.length + 1, text: '', is_critical: false, benchmark_seconds: 3 }]);

  const handleSave = async () => {
    if (!title.trim() || items.length === 0 || items.some(i => !i.text.trim())) return;
    setSaving(true);
    try {
      const payload = {
        aircraft_id: aircraftId,
        title: title.trim(),
        emergency_type: emergencyType,
        description: description.trim() || null,
        benchmark_seconds: benchmarkSeconds,
        items: items.map((item, idx) => ({ ...item, step_number: idx + 1 })),
      };
      if (procedure?.id) {
        await emergencyService.updateProcedure(procedure.id, payload);
      } else {
        await emergencyService.createProcedure(payload);
      }
      onSave();
    } catch (err) {
      console.error('[EmergencyProcedureManager] save error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      style={{ position:'fixed', inset:0, zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:16, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(6px)' }}>
      <motion.div initial={{ scale:0.92, y:20 }} animate={{ scale:1, y:0 }} exit={{ scale:0.92, y:20 }}
        style={{ width:'100%', maxWidth:560, maxHeight:'90vh', overflow:'auto', borderRadius:24, background:'white', boxShadow:'0 24px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ height:4, background:'linear-gradient(90deg,#ef4444,#f97316)' }} />
        <div style={{ padding:'24px 24px 20px', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ margin:0, fontSize:18, fontWeight:800, color:'#0f172a' }}>
            {procedure?.id ? 'Editar' : 'Crear'} procedimiento
          </h3>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', padding:4 }}>
            <X size={20} color="#94a3b8" />
          </button>
        </div>

        <div style={{ padding:'20px 24px', display:'flex', flexDirection:'column', gap:16 }}>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Título del procedimiento"
            style={{ width:'100%', padding:'12px 16px', borderRadius:14, border:'1px solid #e2e8f0', fontSize:15, fontWeight:700, fontFamily:'inherit' }} />

          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {Object.entries(EMERGENCY_TYPES).map(([key, { label, emoji, color }]) => (
              <button key={key} onClick={() => setEmergencyType(key)}
                style={{ padding:'8px 14px', borderRadius:12, border: emergencyType === key ? `2px solid ${color}` : '1px solid #e2e8f0',
                  background: emergencyType === key ? `${color}15` : 'white', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit',
                  color: emergencyType === key ? color : '#64748b', display:'flex', alignItems:'center', gap:6 }}>
                <span>{emoji}</span> {label}
              </button>
            ))}
          </div>

          <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Descripción (opcional)" rows={2}
            style={{ width:'100%', padding:'10px 14px', borderRadius:12, border:'1px solid #e2e8f0', fontSize:13, fontFamily:'inherit', resize:'vertical' }} />

          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:13, fontWeight:600, color:'#475569' }}>Benchmark total:</span>
            <input type="number" value={benchmarkSeconds} min={5} max={600}
              onChange={e => setBenchmarkSeconds(parseInt(e.target.value) || 30)}
              style={{ width:80, padding:'6px 12px', borderRadius:10, border:'1px solid #e2e8f0', fontSize:14, fontFamily:'inherit', textAlign:'center' }} />
            <span style={{ fontSize:12, color:'#94a3b8' }}>segundos</span>
          </div>

          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
              <span style={{ fontSize:14, fontWeight:700, color:'#0f172a' }}>Pasos ({items.length})</span>
              <button onClick={addItem}
                style={{ display:'flex', alignItems:'center', gap:4, padding:'6px 12px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#2563eb,#7c3aed)', color:'white', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
                <Plus size={14} /> Agregar paso
              </button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {items.map((step, idx) => (
                <StepRow key={idx} step={step} index={idx} total={items.length} onUpdate={updateItem} onRemove={removeItem} />
              ))}
            </div>
          </div>
        </div>

        <div style={{ padding:'16px 24px 24px', display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button onClick={onClose}
            style={{ padding:'12px 20px', borderRadius:14, border:'1px solid #e2e8f0', background:'white', color:'#64748b', fontWeight:600, fontSize:14, cursor:'pointer', fontFamily:'inherit' }}>
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving || !title.trim() || items.length === 0}
            style={{ padding:'12px 24px', borderRadius:14, border:'none', background: saving ? '#94a3b8' : 'linear-gradient(135deg,#ef4444,#f97316)', color:'white', fontWeight:700, fontSize:14, cursor: saving ? 'not-allowed' : 'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:6 }}>
            {saving && <Loader size={14} className="animate-spin" />}
            {procedure?.id ? 'Guardar cambios' : 'Crear procedimiento'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Procedure list for a single aircraft (inline) ────────────────
const ProcedureList = ({ aircraftId, aircraftName, darkMode }) => {
  const { t } = useTranslation('admin');
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editProc, setEditProc] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [cockpitUploading, setCockpitUploading] = useState(false);
  const [cockpitSuccess, setCockpitSuccess] = useState(false);
  const [disableTargetId, setDisableTargetId] = useState(null);
  const [disabling, setDisabling] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await emergencyService.listProcedures(aircraftId);
      setProcedures(res.data?.data || []);
    } catch (err) {
      console.error('[EmergencyProcedureManager] load error:', err);
    } finally {
      setLoading(false);
    }
  }, [aircraftId]);

  useEffect(() => { load(); }, [load]);

  const handleDeleteClick = (id) => setDisableTargetId(id);
  const handleDeleteConfirm = async () => {
    if (!disableTargetId) return;
    setDisabling(true);
    try {
      await emergencyService.deleteProcedure(disableTargetId);
      load();
    } catch (err) {
      console.error('[EmergencyProcedureManager] delete error:', err);
    } finally {
      setDisabling(false);
      setDisableTargetId(null);
    }
  };

  const handleScan = async (file) => {
    setScanning(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1];
        const mediaType = file.type || 'image/jpeg';
        const res = await emergencyService.scanProcedure({ image: base64, mediaType });
        setScanResult(res.data?.data || null);
        setScanning(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('[EmergencyProcedureManager] scan error:', err);
      setScanning(false);
    }
  };

  const handleCockpitUpload = async (file) => {
    setCockpitUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1];
        await emergencyService.uploadCockpit(aircraftId, { image: base64, mediaType: file.type || 'image/jpeg' });
        setCockpitUploading(false);
        setCockpitSuccess(true);
        setTimeout(() => setCockpitSuccess(false), 3000);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('[EmergencyProcedureManager] cockpit upload error:', err);
      setCockpitUploading(false);
    }
  };

  const handleSavedFromScan = () => {
    setScanResult(null);
    setShowForm(false);
    setEditProc(null);
    load();
  };

  return (
    <div>
      {cockpitSuccess && (
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 16px', borderRadius:14, background:'rgba(22,163,74,0.1)', border:'1px solid rgba(22,163,74,0.25)', marginBottom:12 }}>
          <span style={{ fontSize:16 }}>✅</span>
          <span style={{ fontSize:13, fontWeight:700, color:'#16a34a' }}>Foto de cockpit subida correctamente</span>
        </div>
      )}
      {/* Actions */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:16 }}>
        <button onClick={() => { setEditProc(null); setShowForm(true); }}
          style={{ display:'flex', alignItems:'center', gap:6, padding:'10px 16px', borderRadius:14, border:'none', background:'linear-gradient(135deg,#ef4444,#f97316)', color:'white', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>
          <Plus size={16} /> Crear manualmente
        </button>
        <label style={{ display:'flex', alignItems:'center', gap:6, padding:'10px 16px', borderRadius:14, border:`1px solid ${darkMode ? 'rgba(255,255,255,0.15)' : '#e2e8f0'}`, background: darkMode ? 'rgba(255,255,255,0.05)' : 'white', color: darkMode ? 'white' : '#475569', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>
          {scanning ? <Loader size={16} className="animate-spin" /> : <Camera size={16} />}
          {scanning ? 'Escaneando...' : 'Escanear manual'}
          <input type="file" accept="image/*" capture="environment" hidden onChange={e => e.target.files[0] && handleScan(e.target.files[0])} />
        </label>
        <label style={{ display:'flex', alignItems:'center', gap:6, padding:'10px 16px', borderRadius:14, border:`1px solid ${darkMode ? 'rgba(255,255,255,0.15)' : '#e2e8f0'}`, background: darkMode ? 'rgba(255,255,255,0.05)' : 'white', color: darkMode ? 'white' : '#475569', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>
          {cockpitUploading ? <Loader size={16} className="animate-spin" /> : <Upload size={16} />}
          Foto cockpit
          <input type="file" accept="image/*" hidden onChange={e => e.target.files[0] && handleCockpitUpload(e.target.files[0])} />
        </label>
      </div>

      {/* Procedures list */}
      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {loading ? (
          <div style={{ textAlign:'center', padding:40 }}>
            <Loader size={24} className="animate-spin" color={darkMode ? '#818cf8' : '#6366f1'} />
          </div>
        ) : procedures.length === 0 ? (
          <div style={{ textAlign:'center', padding:'32px 20px' }}>
            <div style={{ fontSize:40, marginBottom:10 }}>🚨</div>
            <p style={{ fontSize:14, fontWeight:600, color: darkMode ? 'rgba(255,255,255,0.6)' : '#64748b', margin:0 }}>
              No hay procedimientos cargados para esta aeronave
            </p>
            <p style={{ fontSize:12, color: darkMode ? 'rgba(255,255,255,0.35)' : '#94a3b8', marginTop:4, margin:'4px 0 0' }}>
              Creá uno manualmente o escaneá una página del manual
            </p>
          </div>
        ) : procedures.map(proc => {
          const type = EMERGENCY_TYPES[proc.emergency_type] || EMERGENCY_TYPES.other;
          const expanded = expandedId === proc.id;
          return (
            <motion.div key={proc.id} layout
              style={{ borderRadius:16, border:`1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`, background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(248,250,252,0.8)', overflow:'hidden' }}>
              <button onClick={() => setExpandedId(expanded ? null : proc.id)}
                style={{ width:'100%', display:'flex', alignItems:'center', gap:12, padding:'14px 16px', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
                <span style={{ fontSize:22 }}>{type.emoji}</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14, fontWeight:700, color: darkMode ? 'white' : '#0f172a' }}>{proc.title}</div>
                  <div style={{ fontSize:12, color: darkMode ? 'rgba(255,255,255,0.45)' : '#94a3b8', marginTop:2 }}>
                    {type.label} · {proc.items?.length || 0} pasos · {proc.benchmark_seconds}s benchmark
                  </div>
                </div>
                {expanded ? <ChevronUp size={18} color="#94a3b8" /> : <ChevronDown size={18} color="#94a3b8" />}
              </button>

              <AnimatePresence>
                {expanded && (
                  <motion.div initial={{ height:0 }} animate={{ height:'auto' }} exit={{ height:0 }} style={{ overflow:'hidden' }}>
                    <div style={{ padding:'0 16px 14px', borderTop:`1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}` }}>
                      {proc.description && (
                        <p style={{ fontSize:13, color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b', margin:'12px 0 8px', lineHeight:1.5 }}>{proc.description}</p>
                      )}
                      <div style={{ display:'flex', flexDirection:'column', gap:6, margin:'10px 0' }}>
                        {(proc.items || []).map((item, idx) => (
                          <div key={item.id || idx} style={{ display:'flex', gap:8, alignItems:'center', padding:'6px 10px', borderRadius:10, background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.8)' }}>
                            <span style={{ fontSize:11, fontWeight:800, color: item.is_critical ? '#dc2626' : '#94a3b8', minWidth:20, textAlign:'center' }}>
                              {idx + 1}
                            </span>
                            <span style={{ flex:1, fontSize:13, fontWeight:600, color: darkMode ? 'white' : '#0f172a' }}>
                              {item.text}
                              {item.detail && <span style={{ fontWeight:400, color: darkMode ? 'rgba(255,255,255,0.45)' : '#94a3b8' }}> — {item.detail}</span>}
                            </span>
                            {item.is_critical && <span style={{ fontSize:10, fontWeight:700, color:'#dc2626', background:'rgba(220,38,38,0.1)', padding:'2px 6px', borderRadius:6 }}>CRÍTICO</span>}
                            {item.is_flexible && <span style={{ fontSize:10, fontWeight:700, color:'#0891b2', background:'rgba(8,145,178,0.1)', padding:'2px 6px', borderRadius:6 }}>FLEXIBLE</span>}
                          </div>
                        ))}
                      </div>
                      <div style={{ display:'flex', gap:8, marginTop:10 }}>
                        <button onClick={() => { setEditProc(proc); setShowForm(true); }}
                          style={{ display:'flex', alignItems:'center', gap:4, padding:'8px 14px', borderRadius:10, border:`1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`, background: darkMode ? 'rgba(255,255,255,0.05)' : 'white', color: darkMode ? 'rgba(255,255,255,0.7)' : '#475569', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                          <Edit2 size={13} /> Editar
                        </button>
                        <button onClick={() => handleDeleteClick(proc.id)}
                          style={{ display:'flex', alignItems:'center', gap:4, padding:'8px 14px', borderRadius:10, border:'1px solid rgba(239,68,68,0.2)', background:'rgba(239,68,68,0.05)', color:'#ef4444', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                          <Trash2 size={13} /> Desactivar
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      {/* Form modal */}
      <AnimatePresence>
        {showForm && (
          <ProcedureFormModal
            procedure={editProc}
            aircraftId={aircraftId}
            onSave={() => { setShowForm(false); setEditProc(null); load(); }}
            onClose={() => { setShowForm(false); setEditProc(null); }}
          />
        )}
      </AnimatePresence>

      {/* Scan result → pre-fill form */}
      <AnimatePresence>
        {scanResult && !showForm && (
          <ProcedureFormModal
            procedure={{ title: scanResult.title, emergency_type: scanResult.emergency_type, items: scanResult.items }}
            aircraftId={aircraftId}
            onSave={handleSavedFromScan}
            onClose={() => setScanResult(null)}
          />
        )}
      </AnimatePresence>

      <ConfirmModal
        isOpen={!!disableTargetId}
        title={t('confirm_modal_emergency_disable_title')}
        message={t('confirm_modal_emergency_disable_message')}
        confirmLabel={t('confirm_modal_emergency_disable_btn')}
        variant="warning"
        isLoading={disabling}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDisableTargetId(null)}
        darkMode={darkMode}
      />
    </div>
  );
};

// ── Main Component — with aircraft selector ───────────────────────
const EmergencyProcedureManager = ({ darkMode }) => {
  const [aircraft, setAircraft] = useState([]);
  const [loadingAircraft, setLoadingAircraft] = useState(true);
  const [selectedAircraftId, setSelectedAircraftId] = useState(null);

  useEffect(() => {
    const loadAircraft = async () => {
      try {
        const res = await aircraftService.getAll({ is_active: true });
        const list = res.data?.data || res.data || [];
        setAircraft(list);
        if (list.length > 0) setSelectedAircraftId(list[0].id);
      } catch (err) {
        console.error('[EmergencyProcedureManager] loadAircraft error:', err);
      } finally {
        setLoadingAircraft(false);
      }
    };
    loadAircraft();
  }, []);

  const selectedAircraft = aircraft.find(a => a.id === selectedAircraftId);

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
        <span style={{ fontSize:22 }}>✈️</span>
        <h3 style={{ margin:0, fontSize:16, fontWeight:800, color: darkMode ? 'white' : '#0f172a' }}>
          Procedimientos por aeronave
        </h3>
      </div>

      {loadingAircraft ? (
        <div style={{ textAlign:'center', padding:30 }}>
          <Loader size={20} className="animate-spin" color="#818cf8" />
        </div>
      ) : aircraft.length === 0 ? (
        <div style={{ textAlign:'center', padding:'32px 20px' }}>
          <p style={{ fontSize:14, color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }}>No hay aeronaves activas</p>
        </div>
      ) : (
        <>
          {/* Aircraft selector tabs */}
          <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:20 }}>
            {aircraft.map(a => {
              const active = a.id === selectedAircraftId;
              return (
                <button key={a.id} onClick={() => setSelectedAircraftId(a.id)}
                  style={{
                    padding:'8px 16px', borderRadius:12, cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700,
                    border: active ? '2px solid #ef4444' : `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                    background: active ? (darkMode ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.08)') : (darkMode ? 'rgba(255,255,255,0.04)' : 'white'),
                    color: active ? '#ef4444' : (darkMode ? 'rgba(255,255,255,0.6)' : '#64748b'),
                    transition: 'all 0.15s',
                  }}>
                  {a.name || `${a.manufacturer} ${a.model_name}`}
                  {a.registration && <span style={{ fontWeight:500, opacity:0.7 }}> · {a.registration}</span>}
                </button>
              );
            })}
          </div>

          {/* Procedure list for selected aircraft */}
          {selectedAircraft && (
            <ProcedureList
              key={selectedAircraftId}
              aircraftId={selectedAircraftId}
              aircraftName={selectedAircraft.name || `${selectedAircraft.manufacturer} ${selectedAircraft.model_name}`}
              darkMode={darkMode}
            />
          )}
        </>
      )}
    </div>
  );
};

export default EmergencyProcedureManager;
